import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../../../config/constants";
import { localizationMapping } from "../../../functions";
// Components
import {
  EvaluationDiv,
  EvaluationInfoDiv,
  EvaluationTitleDiv,
  EvaluationTitle,
  DetailsDiv,
  DetailsButton,
  IconImg,
  EvaluationDataDiv,
  ColumnsDiv,
  Column,
  Box,
  Text,
  EditButton,
  EditImg,
} from "./styles";
import Loader from "../../../../loader";
import UseFetch from "../../../../../config/useFetch";
import EvaluationsHeader from "../evaluationsHeader";
import WarningMessage from "../../../../warningMessage";
import AddEvaluation from "../addEvaluation";
import UpdateEvaluation from "../updateEvaluation";
// Assets
import IcoArrowUp from "../../../../../assets/icons/ico_arrow_up_blue.svg";
import IcoArrowDown from "../../../../../assets/icons/ico_arrow_down_blue.svg";
import IcoCross from "../../../../../assets/icons/ico_cross.svg";
import EditImgAsset from "../../../../../assets/icons/btn_edit_user_blue.svg";

const EvaluationsData = ({ currentSessionDate }) => {
  const [evaluations, setEvaluations] = useState([]);
  const [isLoadingEvaluations, setIsLoadingEvaluations] = useState(true);
  const [isShowingEvaluationDetails, setIsShowingEvaluationDetails] = useState(false);
  const [someDifferentEvaluationValue, setSomeDifferentEvaluationValue] = useState(false);
  const [isShowingNewEvaluationPopup, setIsShowingNewEvaluationPopup] = useState(false);
  const [isShowingUpdateEvaluationPopup, setIsShowingUpdateEvaluationPopup] = useState(false);
  const [currentEvaluation, setCurrentEvaluation] = useState({});
  const [evaluationIndex, setEvaluationIndex] = useState(1);
  const [recharge, setRecharge] = useState(false);
  const [evaluationKeys] = useState([
    "RASS",
    "Delirium",
    "Dyspnoea",
    "Pain",
    "AnxiousSymptomatology",
    "DepressiveSymptoms",
    "Sleep",
    "MechanicalVentilation",
    "Position",
    "Mobility",
    "Observations",
  ]);
  const { patientId } = useParams();
  const [t] = useTranslation("global");

  // Fetching evaluations
  useEffect(() => {
    setIsLoadingEvaluations(true);
    if (currentSessionDate) {
      UseFetch("get", `${WEB_SERVICE_MIMS}/icu/get-evaluations/${patientId}`)
        .then(({ data }) => {
          // Filtering evaluations by date
          const evaluations = data.filter((item) => item.EVALUATION_DATE.substring(0, 10) === currentSessionDate);

          const isSomeDifferentEvaluationValue = () => {
            if (!Array.isArray(evaluations) || evaluations.length < 2) return false;
            const keysToCheck = ["RASS", "Delirium", "Sleep", "MechanicalVentilation", "Position", "Mobility"];
            const reference = evaluations[0];
            for (let i = 1; i < evaluations.length; i++) {
              for (const key of keysToCheck) {
                if (evaluations[i][key] !== reference[key]) {
                  return true;
                }
              }
            }
            return false;
          };

          setEvaluations(evaluations);
          setSomeDifferentEvaluationValue(isSomeDifferentEvaluationValue());
          setIsLoadingEvaluations(false);
        })
        .catch((error) => {
          console.log("Get evaluations: ", error);
        });
    }

    return () => {
      setEvaluations([]);
      setIsLoadingEvaluations(true);
    };
  }, [patientId, currentSessionDate, recharge]);

  const renderTitle = () => {
    return (
      <EvaluationTitleDiv>
        <EvaluationTitle>{t("evaluation")}</EvaluationTitle>
      </EvaluationTitleDiv>
    );
  };

  const renderDetailButtons = () => {
    return (
      <>
        <DetailsDiv>
          <DetailsButton onClick={() => setIsShowingNewEvaluationPopup(true)}>
            <IconImg src={IcoCross} alt="ico-cross" />
          </DetailsButton>
          {Boolean(evaluations.length) && (
            <DetailsButton onClick={() => setIsShowingEvaluationDetails(!isShowingEvaluationDetails)}>
              <IconImg src={isShowingEvaluationDetails ? IcoArrowUp : IcoArrowDown} alt="ico-arrow" />
            </DetailsButton>
          )}
        </DetailsDiv>
        {isShowingNewEvaluationPopup && (
          <AddEvaluation
            currentSessionDate={currentSessionDate}
            isShowingNewEvaluationPopup={isShowingNewEvaluationPopup}
            setIsShowingNewEvaluationPopup={setIsShowingNewEvaluationPopup}
            recharge={recharge}
            setRecharge={setRecharge}
          />
        )}
      </>
    );
  };

  const renderEvaluations = () => {
    const renderKeys = () => {
      return (
        <Column background="#F2F2F2F2" width={evaluations.length === 1 ? "50%" : "30%"}>
          <Box topShadow background="#F2F2F2F2" />
          {evaluationKeys.map((evaluationKey) => {
            return (
              <Box key={evaluationKey} background="#F2F2F2F2">
                <Text strong>{t(evaluationKey)}</Text>
              </Box>
            );
          })}
        </Column>
      );
    };

    const renderEvaluationData = () => {
      const setUpdatePopup = (evaluation, evaluationIndex) => {
        setCurrentEvaluation(evaluation, evaluationIndex);
        setEvaluationIndex(evaluationIndex + 1);
        setIsShowingUpdateEvaluationPopup(true);
      };

      return evaluations.map((evaluation, evaluationIndex) => {
        const evaluationUnit = evaluation.EVALUATION_UNIT;

        const renderTitle = () => {
          const time = moment(evaluation.EVALUATION_DATE).format("HH:mm");
          if (evaluationIndex === 0) return `PRE — ${time}`;
          if (evaluationIndex === 1) return `POST — ${time}`;
          return time;
        };

        const getColumnWidth = () => {
          if (evaluations.length > 2) return "199px";
          const widthMapping = ["100%", "50%"];
          return widthMapping[evaluations.length - 1];
        };

        return (
          <Column key={evaluationUnit} width={getColumnWidth()}>
            <Box justify="space-between" topShadow borderLeft={evaluationIndex === 0 && "2px solid #fff"} background="#F2F2F2F2">
              <Text strong>{renderTitle()}</Text>
              <EditButton onClick={() => setUpdatePopup(evaluation, evaluationIndex)}>
                <EditImg src={EditImgAsset} alt="ico-edit" />
              </EditButton>
            </Box>
            {evaluationKeys.map((evaluationKey, evaluationKeyIndex) => {
              const isCorrectValue = () => {
                if (evaluationIndex === 0) return true;
                const firstEvaluationValue = evaluations[0][evaluationKey];
                const currentEvaluationValue = evaluation[evaluationKey];
                const comparableKeys = ["RASS", "Delirium", "Sleep", "MechanicalVentilation", "Position", "Mobility"];
                if (firstEvaluationValue !== currentEvaluationValue && comparableKeys.includes(evaluationKey)) {
                  return false;
                }
                return true;
              };

              const localizedKeys = ["Delirium", "MechanicalVentilation", "Position", "Mobility"];

              return (
                <Box
                  key={`${evaluationUnit}_${evaluationKey}`}
                  background="#fff"
                  borderBottom={evaluationKeyIndex + 1 !== evaluationKeys.length && "2px solid #F2F2F2F2"}
                >
                  <Text isError={!isCorrectValue()}>
                    {localizedKeys.includes(evaluationKey) ? t(localizationMapping(evaluation[evaluationKey])) : evaluation[evaluationKey]}
                  </Text>
                </Box>
              );
            })}
          </Column>
        );
      });
    };

    return (
      <EvaluationDataDiv>
        {renderKeys()}
        <ColumnsDiv width={evaluations.length === 1 ? "50%" : "70%"}>{renderEvaluationData()}</ColumnsDiv>
      </EvaluationDataDiv>
    );
  };

  return (
    <>
      {isLoadingEvaluations ? (
        <Loader />
      ) : (
        <>
          <EvaluationsHeader />
          {Boolean(evaluations.length === 1) && <WarningMessage message={t("rgsicu_oneEvaluationWarning")} margin="10px auto" />}
          {Boolean(evaluations.length > 2) && <WarningMessage message={t("rgsicu_moreThanTwoEvaluationsWarning")} margin="10px auto" />}
          {someDifferentEvaluationValue && <WarningMessage message={t("rgsicu_differentEvaluationValuesWarning")} margin="10px auto" />}
          <EvaluationDiv>
            <EvaluationInfoDiv>
              {renderTitle()}
              {renderDetailButtons()}
            </EvaluationInfoDiv>
            {isShowingEvaluationDetails && Boolean(evaluations.length) && renderEvaluations()}
          </EvaluationDiv>
          {isShowingUpdateEvaluationPopup && (
            <UpdateEvaluation
              evaluation={currentEvaluation}
              evaluationIndex={evaluationIndex}
              isShowingUpdateEvaluationPopup={isShowingUpdateEvaluationPopup}
              setIsShowingUpdateEvaluationPopup={setIsShowingUpdateEvaluationPopup}
              recharge={recharge}
              setRecharge={setRecharge}
            />
          )}
        </>
      )}
    </>
  );
};

export default EvaluationsData;
